/**
 * Client-minted identities (`STD-002 §5`, `ARCH-002 §1`).
 *
 * A local write gets its id **on the device, before the write is attempted**, and that single fact is what
 * makes an offline outbox safe: the same entry cannot be created twice however many times the queue sends
 * it, because the server recognises the id it already has (`FEAT-011 §5.2`, `FEAT-014 §2.1`).
 */

/**
 * A new time-ordered id, as a UUID string.
 *
 * **UUID version 7**, which is what `ARCH-002 §1` means by "ULID-shaped, sequential" and exactly what the
 * server's own `UlidIdGenerator` produces: a 48-bit millisecond timestamp followed by randomness, in that
 * order, so the value sorts by the moment it was made.
 *
 * Matching the server's shape is the point rather than a nicety. An entry created on a phone and one
 * created on the server have to be the same kind of thing — they land in the same index, and an id whose
 * ordering was private to one of them would scatter the other's inserts across the b-tree.
 *
 * `crypto.randomUUID()` is a **version 4** and is not time-ordered. It was what `T-0214` used, and it works
 * — a v4 is unique — but it gives up the ordering the schema is built around.
 *
 * @param now The moment to stamp, for a test that needs two ids in a known order.
 */
export function newId(now: number = Date.now()): string {
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);

  // 48 bits of milliseconds, big-endian, in the first six bytes. `now` is well inside the safe integer
  // range until the year 10889, so the arithmetic below does not need BigInt.
  bytes[0] = Math.floor(now / 2 ** 40) & 0xff;
  bytes[1] = Math.floor(now / 2 ** 32) & 0xff;
  bytes[2] = Math.floor(now / 2 ** 24) & 0xff;
  bytes[3] = Math.floor(now / 2 ** 16) & 0xff;
  bytes[4] = Math.floor(now / 2 ** 8) & 0xff;
  bytes[5] = now & 0xff;

  // Version 7 in the high nibble of byte 6, and the RFC 4122 variant in the top two bits of byte 8. Without
  // both, this is a random string that merely looks like a UUID, and a parser is entitled to reject it.
  //
  // The non-null assertions are safe by construction: the array was made with sixteen elements. TypeScript
  // cannot see that under `noUncheckedIndexedAccess`, which is the right default everywhere else.
  bytes[6] = (bytes[6]! & 0x0f) | 0x70;
  bytes[8] = (bytes[8]! & 0x3f) | 0x80;

  const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');

  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}
